import { useState, useEffect } from 'react'
import { api } from '../lib/api'
import { useDemoMode } from '../hooks/usePreserveQueryParams'
import { demoCategoryPerformance } from '../utils/demoData'
import {
  Sparkles,
  Tag,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle,
  Loader2,
  Lightbulb,
  Copy,
  Check
} from 'lucide-react'
import Modal from './Modal'

interface SEORecommendations {
  keywords: string[]
  contentTypes: string[]
  suggestedTopics: string[]
}

interface BrandCategory {
  id: number
  category_name: string
  category_slug: string
  description: string
  example_queries: string[]
  seo_recommendations?: SEORecommendations | null
  is_active: boolean
  created_at: string
}

interface CategoryPerformance extends BrandCategory {
  query_count: number
  your_mentions: number
  competitor_mentions: number
  your_mention_rate: string
  competitor_mention_rate: string
  content_gap: boolean
  status: 'competitive' | 'content_gap' | 'emerging'
  competitors: Array<{
    id: string
    name: string
    mention_count: number
  }>
}

interface CategoryDiscoveryProps {
  userBrandId: string
}

export default function CategoryDiscovery({ userBrandId }: CategoryDiscoveryProps) {
  const { isDemoMode } = useDemoMode()
  const [categories, setCategories] = useState<CategoryPerformance[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedCategory, setSelectedCategory] = useState<CategoryPerformance | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [copiedKeyword, setCopiedKeyword] = useState<string | null>(null)

  useEffect(() => {
    fetchCategories()
  }, [userBrandId, isDemoMode])

  const fetchCategories = async () => {
    try {
      setLoading(true)

      // Use demo data if in demo mode
      if (isDemoMode) {
        setCategories(demoCategoryPerformance)
        setLoading(false)
        return
      }

      // Fetch categories and their performance
      const response = await api.get(`/categories/${userBrandId}/performance`)

      if (response.data.success) {
        setCategories(response.data.performance)
      }
    } catch (err: any) {
      console.error('Error fetching categories:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleCopyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
    setCopiedKeyword(text)
    setTimeout(() => setCopiedKeyword(null), 2000)
  }

  const getPerformanceIcon = (category: CategoryPerformance) => {
    if (category.status === 'emerging') {
      return <Sparkles className="w-5 h-5 text-blue-600" />
    } else if (category.status === 'content_gap') {
      return <TrendingDown className="w-5 h-5 text-red-600" />
    } else {
      return <TrendingUp className="w-5 h-5 text-green-600" />
    }
  }

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 text-indigo-600 animate-spin" />
          <span className="ml-3 text-gray-600">Loading brand categories...</span>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">

      {/* Categories Performance - Compact Table */}
      {categories.length > 0 ? (
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">
              Brand-Relevant Search Categories
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              Track where you appear vs competitors in each market segment
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Your Mentions
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Competitor Mentions
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {categories.map((category) => (
                    <tr
                      key={category.id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => {
                        setSelectedCategory(category)
                        setModalOpen(true)
                      }}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-3">
                          {getPerformanceIcon(category)}
                          <div className="flex-1">
                            <div className="text-sm font-medium text-gray-900">
                              {category.category_name}
                            </div>
                            <div className="text-xs text-gray-500 mt-0.5">
                              {category.description}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-baseline space-x-2">
                          <span className="text-lg font-semibold text-indigo-600">
                            {category.your_mention_rate}%
                          </span>
                        </div>
                        <div className="text-xs text-gray-500">
                          {category.your_mentions} of {category.query_count}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-baseline space-x-2">
                          <span className="text-lg font-semibold text-gray-700">
                            {category.competitor_mention_rate}%
                          </span>
                        </div>
                        <div className="text-xs text-gray-500">
                          {category.competitor_mentions} of {category.query_count}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {category.status === 'content_gap' && (
                          <span className="inline-flex items-center px-2.5 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-full">
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            Content Gap
                          </span>
                        )}
                        {category.status === 'competitive' && (
                          <span className="inline-flex items-center px-2.5 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Competitive
                          </span>
                        )}
                        {category.status === 'emerging' && (
                          <span className="inline-flex items-center px-2.5 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                            <Sparkles className="w-3 h-3 mr-1" />
                            Opportunity
                          </span>
                        )}
                      </td>
                    </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <Tag className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            No Categories Yet
          </h3>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            Click "Discover Categories" to analyze your brand and market to generate
            brand-specific query categories using AI.
          </p>
        </div>
      )}

      {/* Category Detail Modal */}
      {selectedCategory && (
        <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} size="3xl">
          <Modal.Header onClose={() => setModalOpen(false)}>
            {getPerformanceIcon(selectedCategory)}
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {selectedCategory.category_name}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {selectedCategory.description}
              </p>
            </div>
          </Modal.Header>
          <Modal.Body className="space-y-6">
              {/* Performance Stats */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Total Queries</p>
                  <p className="text-2xl font-bold text-gray-900">{selectedCategory.query_count}</p>
                </div>
                <div className="bg-indigo-50 rounded-lg p-4">
                  <p className="text-xs text-indigo-600 uppercase tracking-wide mb-1">Your Mention Rate</p>
                  <p className="text-2xl font-bold text-indigo-600">{selectedCategory.your_mention_rate}%</p>
                  <p className="text-xs text-gray-600 mt-1">
                    {selectedCategory.your_mentions} of {selectedCategory.query_count} queries
                  </p>
                </div>
                <div className="bg-purple-50 rounded-lg p-4">
                  <p className="text-xs text-purple-600 uppercase tracking-wide mb-1">Competitor Rate</p>
                  <p className="text-2xl font-bold text-purple-600">{selectedCategory.competitor_mention_rate}%</p>
                  <p className="text-xs text-gray-600 mt-1">
                    {selectedCategory.competitor_mentions} of {selectedCategory.query_count} queries
                  </p>
                </div>
              </div>

              {/* Competitors Mentioned */}
              {selectedCategory.competitors && selectedCategory.competitors.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                    <TrendingDown className="w-4 h-4" />
                    <span>Competitors Mentioned ({selectedCategory.competitors.length})</span>
                  </h3>
                  <div className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
                    <div className="max-h-64 overflow-y-auto">
                      <table className="min-w-full">
                        <thead className="bg-gray-100 sticky top-0 z-0">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Brand
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Mentions
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Rate
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                        {selectedCategory.competitors.map((competitor) => {
                          const mentionRate = ((competitor.mention_count / selectedCategory.query_count) * 100).toFixed(1);
                          return (
                            <tr key={competitor.id} className="hover:bg-gray-50">
                              <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                {competitor.name}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {competitor.mention_count} of {selectedCategory.query_count}
                              </td>
                              <td className="px-4 py-3 text-sm">
                                <span className="inline-flex items-center px-2 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded-full">
                                  {mentionRate}%
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    </div>
                  </div>
                </div>
              )}

              {/* Content Gap Alert */}
              {selectedCategory.content_gap && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <h3 className="text-sm font-semibold text-red-900 mb-2">Content Gap Detected</h3>
                      <p className="text-sm text-red-800 mb-3">
                        Competitors appear more frequently than your brand in this category.
                        Your mention rate ({selectedCategory.your_mention_rate}%) is lower than competitors ({selectedCategory.competitor_mention_rate}%).
                      </p>

                      <div className="bg-white rounded-lg p-4 border border-red-200">
                        <div className="flex items-start space-x-2 mb-3">
                          <Lightbulb className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                          <h4 className="text-xs font-semibold text-red-900 uppercase tracking-wide">Recommendations</h4>
                        </div>
                        <ul className="space-y-2 text-sm text-red-900">
                          <li className="flex items-start space-x-2">
                            <span className="text-red-600 font-bold">•</span>
                            <span>Create comprehensive content addressing the example queries below</span>
                          </li>
                          <li className="flex items-start space-x-2">
                            <span className="text-red-600 font-bold">•</span>
                            <span>Update your LLMS.txt to include key information about {selectedCategory.category_name.toLowerCase()}</span>
                          </li>
                          <li className="flex items-start space-x-2">
                            <span className="text-red-600 font-bold">•</span>
                            <span>Develop case studies, guides, or resources specific to this category</span>
                          </li>
                          <li className="flex items-start space-x-2">
                            <span className="text-red-600 font-bold">•</span>
                            <span>Ensure your brand is mentioned prominently in content related to these topics</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Competitive Position */}
              {selectedCategory.status === 'competitive' && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <h3 className="text-sm font-semibold text-green-900 mb-1">Competitive Position</h3>
                      <p className="text-sm text-green-800">
                        Your brand is performing well in this category with a {selectedCategory.your_mention_rate}% mention rate,
                        matching or exceeding competitor presence.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Emerging Opportunity */}
              {selectedCategory.status === 'emerging' && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <Sparkles className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <h3 className="text-sm font-semibold text-blue-900 mb-2">Emerging Opportunity</h3>
                      <p className="text-sm text-blue-800 mb-3">
                        Neither your brand nor competitors are currently mentioned in AI responses for this category.
                        This represents an untapped market opportunity to establish thought leadership.
                      </p>

                      <div className="bg-white rounded-lg p-4 border border-blue-200">
                        <div className="flex items-start space-x-2 mb-3">
                          <Lightbulb className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                          <h4 className="text-xs font-semibold text-blue-900 uppercase tracking-wide">Recommendations</h4>
                        </div>
                        <ul className="space-y-2 text-sm text-blue-900">
                          <li className="flex items-start space-x-2">
                            <span className="text-blue-600 font-bold">•</span>
                            <span>First-mover advantage: Create authoritative content before competitors</span>
                          </li>
                          <li className="flex items-start space-x-2">
                            <span className="text-blue-600 font-bold">•</span>
                            <span>Establish your brand as the go-to resource for {selectedCategory.category_name.toLowerCase()}</span>
                          </li>
                          <li className="flex items-start space-x-2">
                            <span className="text-blue-600 font-bold">•</span>
                            <span>Build comprehensive guides, case studies, and thought leadership content</span>
                          </li>
                          <li className="flex items-start space-x-2">
                            <span className="text-blue-600 font-bold">•</span>
                            <span>Update LLMS.txt with detailed information to position your brand as the category leader</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Example Queries */}
              {selectedCategory.example_queries && selectedCategory.example_queries.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                    <Tag className="w-4 h-4" />
                    <span>Example Queries</span>
                  </h3>
                  <div className="space-y-2">
                    {selectedCategory.example_queries.map((query, idx) => (
                      <div
                        key={idx}
                        className="bg-gray-50 rounded-lg p-3 border border-gray-200 hover:border-indigo-300 transition-colors"
                      >
                        <p className="text-sm text-gray-700 italic">"{query}"</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* SEO Recommendations */}
              {selectedCategory.seo_recommendations && selectedCategory.seo_recommendations.keywords && selectedCategory.seo_recommendations.keywords.length > 0 && (
                <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg p-5 border border-indigo-200">
                  <div className="flex items-start space-x-3 mb-3">
                    <Sparkles className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <h3 className="text-sm font-semibold text-indigo-900 mb-1">Recommended Keywords for Content</h3>
                      <p className="text-xs text-indigo-700">
                        Click any keyword to copy to clipboard
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {selectedCategory.seo_recommendations.keywords.map((keyword, idx) => {
                      const isCopied = copiedKeyword === keyword;
                      return (
                        <button
                          key={idx}
                          onClick={() => handleCopyToClipboard(keyword)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white text-indigo-700 text-xs font-medium rounded-lg border border-indigo-200 hover:bg-indigo-50 hover:border-indigo-300 transition-all cursor-pointer group"
                          title="Click to copy"
                        >
                          <span>{keyword}</span>
                          {isCopied ? (
                            <Check className="w-3.5 h-3.5 text-green-600" />
                          ) : (
                            <Copy className="w-3.5 h-3.5 opacity-40 group-hover:opacity-100 transition-opacity" />
                          )}
                        </button>
                      );
                    })}
                  </div>

                  {selectedCategory.seo_recommendations.contentTypes && selectedCategory.seo_recommendations.contentTypes.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-indigo-200">
                      <p className="text-xs font-semibold text-indigo-900 mb-2">Recommended Content Types:</p>
                      <div className="flex flex-wrap gap-2">
                        {selectedCategory.seo_recommendations.contentTypes.map((type, idx) => (
                          <span
                            key={idx}
                            className="inline-flex items-center px-2 py-1 bg-indigo-100 text-indigo-800 text-xs font-medium rounded"
                          >
                            {type}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {selectedCategory.seo_recommendations.suggestedTopics && selectedCategory.seo_recommendations.suggestedTopics.length > 0 && (
                    <div className="mt-3">
                      <p className="text-xs font-semibold text-indigo-900 mb-2">Suggested Topics (click to copy):</p>
                      <div className="flex flex-wrap gap-2">
                        {selectedCategory.seo_recommendations.suggestedTopics.map((topic, idx) => {
                          const isCopied = copiedKeyword === topic;
                          return (
                            <button
                              key={idx}
                              onClick={() => handleCopyToClipboard(topic)}
                              className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-purple-100 text-purple-800 text-left text-xs font-medium rounded-lg hover:bg-purple-200 transition-all cursor-pointer group"
                              title="Click to copy"
                            >
                              <span>{topic}</span>
                              {isCopied ? (
                                <Check className="w-3 h-3 text-purple-900" />
                              ) : (
                                <Copy className="w-3 h-3 opacity-40 group-hover:opacity-100 transition-opacity" />
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

          </Modal.Body>
          <Modal.Footer className="flex justify-end">
            <button
              onClick={() => setModalOpen(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Close
            </button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  )
}
